import React, { useMemo } from "react";
import styled from "@emotion/styled";
import { useResizeDetector } from "react-resize-detector";
import { useI18n } from "@core/i18n/i18n";
import { useOnceEffect } from "@core/hooks/use-once-effect";
import { isNotNil } from "@utils/assert";
import { colors } from "@ui/style/colors";
import { physioMeTabletBreakpoint, physioMeMobileBreakpoint, row, stack } from "@ui/style";
import { getScrollbarStyle } from "@ui/style/scrollbar";
import { useDimensions } from "@ui/hooks/use-dimensions";
import { NetworkError } from "@ui/components/common/error";
import { PageHeader } from "@ui/components/common/page-header";
import { EmptySearch } from "@ui/pages/exercise/components/empty-search";
import { ExerciseCounts } from "@ui/pages/exercise/components/exercise-counts";
import { ExerciseCard } from "@ui/pages/exercise/components/exercise-card";
import { useExercisePage } from "@ui/pages/exercise/hooks/use-exercise-page";
import { FilterListingPanel } from "@ui/pages/exercise/components/filter-listing-panel";
import { SearchFilter } from "@ui/pages/exercise/components/search-filter";
import { SelectedExerciseCount } from "@ui/pages/exercise/components/selected-exercise-count";
import { SelectedExerciseListingPanel } from "@ui/pages/exercise/components/selected-exercise-listing-panel";
import { CreateProgramButton } from "@ui/pages/exercise/components/create-program-button";
import { ActiveFilterListing } from "@ui/pages/exercise/components/active-filter-listing";

export const ExerciseListingPage = (): JSX.Element => {
	const { format } = useI18n();
	const { isDesktop } = useDimensions();
	const { filteredExercises, selectedExercises, refreshExercises, isLoading, isError } = useExercisePage();
	const displayEmptySearch = useMemo(
		() => !isLoading && filteredExercises.length === 0,
		[isLoading, filteredExercises.length],
	);
	const { width: contentContainerWidth, ref: contentContainerRef } = useResizeDetector();
	const { height: bottomContainerHeight, ref: bottomContainerRef } = useResizeDetector();
	const isBottomVisible = useMemo(() => selectedExercises.length > 0, [selectedExercises.length]);

	useOnceEffect(() => {
		refreshExercises();
	});

	const exerciseList = useMemo(
		() =>
			isLoading
				? Array.from({ length: 10 }, (_, index) => <ExerciseCard key={index} loading />)
				: filteredExercises.map(exercise => <ExerciseCard key={exercise.id} exercise={exercise} />),
		[isLoading, filteredExercises],
	);

	return (
		<Container>
			{isError ? (
				<ErrorContainer>
					<NetworkError onRefresh={refreshExercises} />
				</ErrorContainer>
			) : (
				<>
					<StyledPageHeader title={format("tabbar.exercises")} />
					<PageBody className="p-card">
						{isDesktop ? (
							<StickyFilterContainer>
								<StyledSearchFilter />
								<StyledFilterListingPanel />
							</StickyFilterContainer>
						) : (
							<MobileFilterContainer>
								<MobileStyledSearchFilter />
								<ActiveFilterListing />
							</MobileFilterContainer>
						)}
						<ContentContainer
							ref={contentContainerRef}
							showPadding={isBottomVisible}
							paddingBottomHeight={bottomContainerHeight}
						>
							<ExerciseListContainer>
								{displayEmptySearch ? (
									<EmptySearchSection>
										<EmptySearch />
									</EmptySearchSection>
								) : (
									<ExerciseListSection>
										<ExerciseCounts loading={isLoading} />
										<ExerciseList>{exerciseList}</ExerciseList>
									</ExerciseListSection>
								)}
							</ExerciseListContainer>
							{isNotNil(contentContainerWidth) && (
								<FixedBottomContainer isVisible={isBottomVisible}>
									{isBottomVisible &&
										(isDesktop ? (
											<BottomContainer ref={bottomContainerRef} width={contentContainerWidth}>
												<StyledSelectedExerciseCount />
												<StyledSelectedExerciseListingPanel />
												<StyledCreateProgramButton />
											</BottomContainer>
										) : (
											<>
												<MobileBottomContainer ref={bottomContainerRef}>
													<StyledSelectedExerciseListingPanel />
													<StyledCreateProgramButton />
												</MobileBottomContainer>
											</>
										))}
								</FixedBottomContainer>
							)}
						</ContentContainer>
					</PageBody>
				</>
			)}
		</Container>
	);
};

const StyledCreateProgramButton = styled(CreateProgramButton)`
	margin-top: 7px;
`;

const StyledSelectedExerciseListingPanel = styled(SelectedExerciseListingPanel)`
	flex: 1;
	padding-bottom: 4px;
`;

const StyledSelectedExerciseCount = styled(SelectedExerciseCount)`
	margin-top: 22px;
`;

const FixedBottomContainer = styled.div<{ isVisible: boolean }>`
	width: 100%;
	min-height: 80px;
	position: fixed;
	bottom: 0;
	z-index: 1;
	background-color: #ffffff;
	border-top: 1px solid var(--primary-25);
	box-shadow: 0px -4px 8px 0px ${colors.black}14;
	transition:
		transform 300ms ease-in-out,
		opacity 300ms ease-in-out;
	transform: ${({ isVisible }) => (isVisible ? "translateY(0)" : "translateY(100%)")};
	opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};

	@media ${physioMeTabletBreakpoint} {
		bottom: 64px;
	}
`;

const BottomContainer = styled.div<{ width: number }>`
	${row("SM")}
	margin-bottom: 4px;
	padding-left: 24px;
	padding-top: 8px;
	${({ width }) => `width: ${width}px;`};
`;

const MobileBottomContainer = styled.div`
	${stack(undefined, "flexstart", "stretch")}
	padding: 8px 16px;
`;

const ContentContainer = styled.div<{ showPadding: boolean; paddingBottomHeight: number | undefined }>`
	display: flex;
	flex-direction: column;
	flex: 1;
	width: 100%;
	padding-bottom: ${({ showPadding, paddingBottomHeight }) =>
		showPadding && paddingBottomHeight ? `${paddingBottomHeight + 13}px` : "0px"};
	transition: padding-bottom 400ms ease-out;

	@media ${physioMeMobileBreakpoint} {
		align-items: center;
	}
`;

const Container = styled.div`
	${stack(undefined, "flexstart", "stretch")}
	flex: 1;
	margin: 0 auto;
	max-width: calc(100vw - 120px);
	width: 100%;

	@media ${physioMeTabletBreakpoint} {
		margin: 0;
		max-width: none;
	}
`;

const StyledPageHeader = styled(PageHeader)`
	@media ${physioMeTabletBreakpoint} {
		padding: 32px 24px;
	}
`;

const PageBody = styled.div`
	display: flex;
	flex-grow: 1;

	@media ${physioMeTabletBreakpoint} {
		${stack()}
		border-radius: 0;
	}
`;

const StickyFilterContainer = styled.div`
	${stack(undefined, undefined, "stretch")}
	border-right: 1px solid var(--surface-border);
	min-width: 390px;
	max-width: 390px;
	max-height: 100vh;
	position: sticky;
	top: 0;
`;

const MobileFilterContainer = styled.div`
	${stack("S")}
	width: 100%;
	padding: 24px 5px 16px 16px;
`;

const StyledSearchFilter = styled(SearchFilter)`
	margin: 24px;
`;

const MobileStyledSearchFilter = styled(SearchFilter)`
	width: 100%;
`;

const StyledFilterListingPanel = styled(FilterListingPanel)`
	${getScrollbarStyle(8)}
	overflow-y: auto;
	padding: 0 24px 24px 24px;
`;

const ExerciseListContainer = styled.div`
	display: flex;
	flex-grow: 1;
	padding: 24px;
	width: 100%;

	@media ${physioMeTabletBreakpoint} {
		padding: 8px 16px 16px;
	}
`;

const EmptySearchSection = styled.div`
	${stack(undefined, undefined, "center")}
	align-self: center;
	flex-grow: 1;
`;

const ExerciseListSection = styled.div`
	${stack(24)}
	width: 100%;
`;

const ErrorContainer = styled.div`
	display: flex;
	flex: 1;
	align-items: center;
	justify-content: center;
`;

const ExerciseList = styled.div`
	${row(24)}
	flex-wrap: wrap;
	width: 100%;

	@media ${physioMeMobileBreakpoint} {
		flex-direction: column;
	}
`;
